import { useGtm } from "@gtm-support/vue-gtm";
import { createSharedComposable } from "@vueuse/core";
import { Logger } from "@/core/utilities";
import { getFileExtension } from "@/shared/thorlabs/shared";

export const useFileDownloadTracker = createSharedComposable(_useFileDownloadTracker);

function _useFileDownloadTracker() {
  const fileExtensions = [
    "cad",
    "csv",
    "docx",
    "dxf",
    "xlsx",
    "xls",
    "exe",
    "pdf",
    "html",
    "htm",
    "json",
    "pptx",
    "rtf",
    "sldprt",
    "step",
    "tgz",
    "txt",
    "xlsm",
    "zar",
    "zmx",
    "zip",
  ];
  const gtm = useGtm();

  if (!gtm) {
    Logger.warn(
      "GTM is not initialized (gtm object in download-counter.ts is undefined). Aborting initializing useFileDownloadTracker...",
    );

    return {
      gtmTrackFileDownload: () => {},
      isDownloadableFile: () => false,
    };
  }

  const gtmTrackFileDownload = (linkUrl: string, isFileName: boolean = false) => {
    if (!isFileName) {
      const guardedLinkUrl = _appendRouteToBaseUrl(linkUrl);
      const urlObject = new URL(guardedLinkUrl);
      const fileName = _getFileNameFromUrl(urlObject);
      const fileExt = getFileExtension(fileName);

      _gtmTrackFileDownload(fileName, fileExt);
      return;
    }

    const fileExt = getFileExtension(linkUrl);
    _gtmTrackFileDownload(linkUrl, fileExt);
  };

  const isDownloadableFile = (url: string, isFileName: boolean = false): boolean => {
    if (url === "#") {
      return false;
    }

    if (!isFileName) {
      const guardedLinkUrl = _appendRouteToBaseUrl(url);
      const urlObject = new URL(guardedLinkUrl);
      const fileName = _getFileNameFromUrl(urlObject);
      const fileExt = getFileExtension(fileName);

      return fileExtensions.includes(fileExt);
    }

    const fileExt = getFileExtension(url);

    return fileExtensions.includes(fileExt);
  };

  const _gtmTrackFileDownload = (fileName: string, fileExt: string) => {
    gtm.push({
      event: "thorlabs_file_download",
      file_name: fileName,
      file_ext: fileExt,
    });
  };

  const _getFileNameFromUrl = (url: URL): string => {
    const path = url.pathname;

    return path.split("/").pop() || "unknown";
  };

  const _appendRouteToBaseUrl = (route: string) => {
    const isAbsoultePath = !!route.match(/^https?:\/\/[^#?/]+/);
    if (isAbsoultePath) {
      return route;
    }

    const baseUrl = `${window.location.protocol}//${window.location.host}/`;

    if (route.startsWith("/")) {
      route = route.substring(1);
    }

    return `${baseUrl}${route}`;
  };

  return {
    gtmTrackFileDownload,
    isDownloadableFile,
  };
}
